import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Typography,
  Empty,
  Alert,
  Divider,
} from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  MarginStatus,
  ResidualTumor,
  ReinterventionNeeded,
  type CreateSurgicalHistopathologyDto,
  type Surgery,
  type SurgicalHistopathology,
} from '../../../../api/dermatoApi';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const MARGIN_OPTIONS = Object.values(MarginStatus).map((v) => ({
  value: v,
  label:
    v === 'FREE' ? 'Margini libere (R0)'
    : v === 'CLOSE' ? 'Apropiate'
    : v === 'INVOLVED' ? 'Invadate (R1/R2)'
    : 'Neevaluate',
}));

const RESIDUAL_OPTIONS = Object.values(ResidualTumor).map((v) => ({
  value: v,
  label:
    v === 'NONE' ? 'Fără tumoră reziduală'
    : v === 'MICROSCOPIC' ? 'Microscopică'
    : v === 'MACROSCOPIC' ? 'Macroscopică'
    : 'Necunoscut',
}));

const REINTERVENTION_OPTIONS = Object.values(ReinterventionNeeded).map((v) => ({
  value: v,
  label: v === 'YES' ? 'Da' : 'Nu',
}));

const Step14SurgicalHisto = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [chosenSurgeryId, setChosenSurgeryId] = useState<string | null>(null);
  const [surgicalHp, setSurgicalHp] = useState<SurgicalHistopathology | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formAntd] = Form.useForm<CreateSurgicalHistopathologyDto>();

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  useEffect(() => {
    if (!lesion?.id) {
      setSurgeries([]);
      setChosenSurgeryId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      try {
        const list = await dermatoApi.surgeries.getByLesion(lesion.id!);
        if (cancelled) return;
        setSurgeries(list);
        if (list.length > 0) {
          setChosenSurgeryId((prev) => prev ?? list[0].id);
        } else {
          setChosenSurgeryId(null);
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id]);

  const surgery = useMemo(
    () => surgeries.find((s) => s.id === chosenSurgeryId) ?? null,
    [surgeries, chosenSurgeryId]
  );

  useEffect(() => {
    if (!surgery?.id) {
      setSurgicalHp(null);
      formAntd.resetFields();
      return;
    }
    let cancelled = false;
    const load = async () => {
      const v = await dermatoApi.surgicalHistopathology.getBySurgery(surgery.id).catch(() => null);
      if (cancelled) return;
      setSurgicalHp(v);
      if (v) {
        formAntd.setFieldsValue({
          ...v,
          resultDate: dayjs(v.resultDate),
        } as any);
      } else {
        formAntd.resetFields();
        formAntd.setFieldsValue({
          resultDate: dayjs(),
          specimenCode: '',
          pathologist: '',
          specimenType: 'Piesă excizională',
          definitiveDiagnosis: '',
          margins: MarginStatus.FREE,
          residualTumor: ResidualTumor.NONE,
          reintervention: ReinterventionNeeded.NO,
        } as any);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [surgery?.id, formAntd]);

  const persist = async (): Promise<boolean> => {
    if (!surgery?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateSurgicalHistopathologyDto = {
        ...values,
        resultDate: (values.resultDate as unknown as dayjs.Dayjs).toISOString(),
      };
      const saved = surgicalHp?.id
        ? await dermatoApi.surgicalHistopathology.update(surgicalHp.id, dto)
        : await dermatoApi.surgicalHistopathology.createForSurgery(surgery.id, dto);
      setSurgicalHp(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) return <Card loading title="HP piesă operatorie" />;

  if (lesions.length === 0) {
    return (
      <Card title="HP piesă operatorie">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <FileSearchOutlined style={{ color: '#1890ff' }} />
          <span>Histopatologie piesă operatorie — 1:1 per chirurgie</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

      {lesion && loadingList && <Alert type="info" message="Se încarcă intervențiile…" />}

      {lesion && !loadingList && surgeries.length === 0 && (
        <Empty description="Nu există intervenții chirurgicale pt această leziune. Mergi la pasul 13." />
      )}

      {lesion && !loadingList && surgeries.length > 0 && (
        <>
          <Form.Item label="Selectează intervenția chirurgicală" required style={{ marginBottom: 16 }}>
            <Select
              value={chosenSurgeryId ?? undefined}
              onChange={setChosenSurgeryId}
              options={surgeries.map((s) => ({
                value: s.id,
                label: `${new Date(s.surgeryDate).toLocaleDateString('ro-RO')} — ${s.procedureType}`,
              }))}
            />
          </Form.Item>

          {surgery && (
            <Form form={formAntd} layout="vertical">
              <Form.Item name="specimenCode" label="Cod specimen (unic global)" rules={[{ required: true }]}>
                <Input placeholder="ex: SP-2026-0042" />
              </Form.Item>
              <Form.Item name="resultDate" label="Data rezultatului HP" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="pathologist" label="Anatomopatolog" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="specimenType" label="Tip piesă" rules={[{ required: true }]}>
                <Input placeholder="ex: Piesă excizională" />
              </Form.Item>
              <Form.Item name="definitiveDiagnosis" label="Diagnostic definitiv" rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="margins" label="Status margini" rules={[{ required: true }]}>
                <Select options={MARGIN_OPTIONS} />
              </Form.Item>
              <Form.Item name="residualTumor" label="Tumoră reziduală" rules={[{ required: true }]}>
                <Select options={RESIDUAL_OPTIONS} />
              </Form.Item>
              <Form.Item name="reintervention" label="Reintervenție necesară" rules={[{ required: true }]}>
                <Select options={REINTERVENTION_OPTIONS} />
              </Form.Item>
              <Form.Item name="observations" label="Observații">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Divider />
              <Form.Item>
                <Space>
                  <Button type="primary" onClick={() => void persist()} loading={saving}>
                    {surgicalHp ? 'Actualizează' : 'Salvează HP piesă'}
                  </Button>
                  {surgicalHp && <Text type="success">✓ HP existent — {surgicalHp.id.slice(0, 8)}</Text>}
                </Space>
              </Form.Item>
            </Form>
          )}
        </>
      )}
    </Card>
  );
};

export default Step14SurgicalHisto;
